import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface PageErrorFallbackProps {
  error: Error;
  reset: () => void;
  /** Heading copy — override for narrower boundaries. */
  title?: string;
  /** Where "Go to Dashboard" points. */
  homeHref?: string;
}

/**
 * The card shown in place of a crashed page. Deliberately plain HTML links and
 * buttons — it must be able to render even when routing or state is the thing
 * that broke.
 */
export const PageErrorFallback: React.FC<PageErrorFallbackProps> = ({
  error,
  reset,
  title = 'Something went wrong',
  homeHref = '/',
}) => (
  <div className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50 px-4 py-12">
    <div
      role="alert"
      className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center"
    >
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="h-8 w-8 text-red-600" aria-hidden="true" />
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
      <p className="text-gray-600 mb-6">
        This section ran into an unexpected problem. The rest of the site is still
        working — you can try again, or head back to the dashboard.
      </p>

      {/* Stack traces are for us, not for end users. */}
      {import.meta.env.DEV && (
        <details className="mb-6 text-left">
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            Error details (development only)
          </summary>
          <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-gray-900 p-3 text-xs text-red-200 whitespace-pre-wrap">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        </details>
      )}

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-center gap-3">
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <RefreshCw className="h-5 w-5 mr-2" aria-hidden="true" />
          Try again
        </button>

        <a
          href={homeHref}
          className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 shadow-sm"
        >
          <Home className="h-5 w-5 mr-2" aria-hidden="true" />
          Go to Dashboard
        </a>
      </div>
    </div>
  </div>
);

export default PageErrorFallback;
